/**
 * Who can sign in (admins only): add someone and send them their set-up link, make them an admin or a
 * lead, give them a new link when they forget their password, or remove them.
 */

import type { PersonRow, Role } from '@mawkeb/shared/api';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { useSession } from '../../state/session';
import { venue } from '../../state/store';
import { Dialog, Menu } from '../Dialog';
import { Back, Dots, Plus } from '../icons';
import { ago } from '../time';
import { Link, PageHeader } from './Shell';

const STATUS: Record<PersonRow['status'], string> = { active: 'Signed up', invited: 'Hasn’t chosen a password yet', removed: 'Removed' };

function SendLink({ name, link, onClose }: { name: string; link: string; onClose: () => void }) {
  const first = name.split(' ')[0];
  const canShare = 'share' in navigator;
  const send = async () => {
    if (canShare) {
      try {
        await navigator.share({ title: 'Venue: choose your password', text: `${first}, choose your password for the Mawkeb venue app:`, url: link });
        return;
      } catch {
        /* closed */
      }
    }
    await navigator.clipboard.writeText(link).catch(() => prompt('Copy this link:', link));
    venue().toast('Link copied.');
  };
  return (
    <Dialog
      title={`Send ${first} their set-up link`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Done
          </button>
          <button type="button" className="primary" onClick={() => void send()}>
            {canShare ? 'Send the link' : 'Copy the link'}
          </button>
        </>
      }
    >
      <p className="lead-text">
        Send this to {first} by text or WhatsApp. They open it and choose their own password; nobody else sees it. It works once, for 7 days.
      </p>
      <input className="link-url" value={link} readOnly onFocus={(e) => e.target.select()} aria-label="Set-up link" />
      <p className="form-note">You won’t see this link again. If it’s lost, make a new one from the list.</p>
    </Dialog>
  );
}

function AddPerson({ onClose, onDone }: { onClose: () => void; onDone: (name: string, link: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('lead');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const r = await api.addPerson(email, name, role);
      onDone(r.person.name, r.link);
    } catch (err) {
      setProblem((err as ApiError).status === 'offline' ? 'There’s no connection. Try again when you’re back online.' : (err as Error).message);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Add someone"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="person-form" className="primary" disabled={busy || !name.trim() || !email.trim()}>
            Add and make their link
          </button>
        </>
      }
    >
      <form id="person-form" className="stack" onSubmit={submit}>
        <label className="input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" required autoFocus />
        </label>
        <label className="input">
          <span>Email</span>
          <input type="email" inputMode="email" autoCapitalize="none" spellCheck={false} autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <fieldset className="choice">
          <legend>They are</legend>
          <label>
            <input type="radio" name="role" checked={role === 'lead'} onChange={() => setRole('lead')} /> A lead: plans layouts in every event
          </label>
          <label>
            <input type="radio" name="role" checked={role === 'admin'} onChange={() => setRole('admin')} /> An admin: also makes events and manages people
          </label>
        </fieldset>
        <p className="form-note">They sign in with this email and a password they choose from the link you’ll send them. No email is sent by the app.</p>
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export function PeoplePage() {
  const me = useSession((s) => s.me);
  const [people, setPeople] = useState<PersonRow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState<{ name: string; link: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setPeople((await api.people()).people);
      setProblem(null);
    } catch (e) {
      setProblem((e as ApiError).status === 'offline' ? 'There’s no connection. The list of people needs one.' : (e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (me?.role === 'admin') void load();
  }, [me?.role, load]);

  if (me?.role !== 'admin') {
    return (
      <div className="page">
        <PageHeader />
        <main className="page-main">
          <div className="empty">
            <h2>Only admins manage who can sign in</h2>
            <Link to="/">Back to the events</Link>
          </div>
        </main>
      </div>
    );
  }

  const act = async (work: () => Promise<unknown>, done?: string) => {
    try {
      await work();
      if (done) venue().toast(done);
      void load();
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const reset = async (p: PersonRow) => {
    if (p.status === 'active' && !confirm(`Make ${p.name} a new set-up link? Their current password stops working and their devices are signed out.`)) return;
    try {
      const { link } = await api.newSetupLink(p.email);
      setSending({ name: p.name, link });
      void load();
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const shown = people?.filter((p) => p.status !== 'removed') ?? [];
  const removed = people?.filter((p) => p.status === 'removed') ?? [];

  return (
    <div className="page">
      <PageHeader />
      <main className="page-main" id="main">
        <Link to="/" className="back-link">
          <Back /> Events
        </Link>
        <div className="page-title">
          <div>
            <p className="kicker">Admins only</p>
            <h1>People who can sign in</h1>
          </div>
          <button type="button" className="primary with-icon" onClick={() => setAdding(true)}>
            <Plus /> Add someone
          </button>
        </div>
        {problem && <p className="form-problem">{problem}</p>}
        {people === null && !problem && <p className="quiet-text">Loading…</p>}
        <ul className="people">
          {shown.map((p) => (
            <li key={p.email} className="person">
              <div className="person-main">
                <strong dir="auto">{p.name}</strong>
                <span className="person-email">{p.email}</span>
              </div>
              <span className={`role ${p.role}`}>{p.role === 'admin' ? 'Admin' : 'Lead'}</span>
              <span className={`status ${p.status}`}>{STATUS[p.status]}</span>
              <span className="quiet-text">{p.seenAt ? `Last here ${ago(p.seenAt)}` : `Added ${ago(p.addedAt)} by ${p.addedBy.name}`}</span>
              {p.email !== me.email ? (
                <Menu
                  label={`${p.name}: more`}
                  icon={<Dots />}
                  items={[
                    { label: p.status === 'invited' ? 'Make a new set-up link' : 'Reset their password', run: () => void reset(p) },
                    p.role === 'lead' ? { label: 'Make them an admin', run: () => void act(() => api.changePerson(p.email, { role: 'admin' }), `${p.name} is now an admin.`) } : { label: 'Make them a lead', run: () => void act(() => api.changePerson(p.email, { role: 'lead' }), `${p.name} is now a lead.`) },
                    { label: 'Remove', danger: true, run: () => confirm(`Remove ${p.name}? They’re signed out at once and can’t sign in again unless added back.`) && void act(() => api.removePerson(p.email), `Removed ${p.name}.`) },
                  ]}
                />
              ) : (
                <span className="quiet-text">You</span>
              )}
            </li>
          ))}
        </ul>
        {removed.length > 0 && (
          <details className="deleted">
            <summary>Removed ({removed.length})</summary>
            <ul>
              {removed.map((p) => (
                <li key={p.email}>
                  <span dir="auto">{p.name}</span>
                  <span className="quiet-text">{p.email}</span>
                </li>
              ))}
            </ul>
            <p className="form-note">To let someone back in, add them again with the same email.</p>
          </details>
        )}
      </main>
      {adding && (
        <AddPerson
          onClose={() => setAdding(false)}
          onDone={(name, link) => {
            setAdding(false);
            setSending({ name, link });
            void load();
          }}
        />
      )}
      {sending && <SendLink name={sending.name} link={sending.link} onClose={() => setSending(null)} />}
    </div>
  );
}
